import React, { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext";
import { ref, set } from "firebase/database";
import { db } from "../../firebase";
import "./CookieBanner.css";

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, val, days = 365) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${val}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
};

export default function CookieBanner() {
  const { currentUser } = useAuth();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if the cookie is set
    const consent = getCookie("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    } else if (currentUser) {
      // If user is logged in, check if we synced their consent to DB
      const syncedKey = `cookieConsentSynced_${currentUser.uid}`;
      if (!localStorage.getItem(syncedKey)) {
        syncConsentToDb(currentUser.uid);
      }
    }
  }, [currentUser]);

  const syncConsentToDb = async (uid) => {
    const now = new Date();
    const storedDate = localStorage.getItem("cookieConsentDate");
    const storedTime = localStorage.getItem("cookieConsentTime");
    const storedTimestamp = localStorage.getItem("cookieConsentTimestamp");

    const baseTime = storedTimestamp ? parseInt(storedTimestamp) : now.getTime();
    const durationDays = 365;
    const expiryDate = new Date(baseTime + (durationDays * 24 * 60 * 60 * 1000));

    const consentData = {
      accepted: true,
      userId: uid,
      timestamp: baseTime,
      date: storedDate || now.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      time: storedTime || now.toLocaleTimeString('en-US', { hour12: false }),
      durationDays: durationDays,
      expiryTimestamp: expiryDate.getTime(),
      expiryDate: expiryDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      expiryTime: expiryDate.toLocaleTimeString('en-US', { hour12: false }),
      userAgent: navigator.userAgent
    };
    try {
      await set(ref(db, `cookieConsents/${uid}`), consentData);
      localStorage.setItem(`cookieConsentSynced_${uid}`, "true");
    } catch (error) {
      console.error("Error syncing cookie consent to DB:", error);
    }
  };

  const handleAccept = async () => {
    // Set browser cookie & localStorage flag
    const durationDays = 365;
    setCookie("cookieConsent", "true", durationDays);
    setIsVisible(false);

    // Save to database
    const now = new Date();
    const consentId = currentUser ? currentUser.uid : `guest_${Math.random().toString(36).substring(2, 15)}`;
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const timeStr = now.toLocaleTimeString('en-US', { hour12: false });
    const timestampVal = now.getTime();
    const expiryDate = new Date(timestampVal + (durationDays * 24 * 60 * 60 * 1000));
    const expiryDateStr = expiryDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const expiryTimeStr = expiryDate.toLocaleTimeString('en-US', { hour12: false });

    // Store in localStorage for future sync upon login
    localStorage.setItem("cookieConsentDate", dateStr);
    localStorage.setItem("cookieConsentTime", timeStr);
    localStorage.setItem("cookieConsentTimestamp", timestampVal.toString());

    const consentData = {
      accepted: true,
      userId: consentId,
      timestamp: timestampVal,
      date: dateStr,
      time: timeStr,
      durationDays: durationDays,
      expiryTimestamp: expiryDate.getTime(),
      expiryDate: expiryDateStr,
      expiryTime: expiryTimeStr,
      userAgent: navigator.userAgent
    };

    try {
      await set(ref(db, `cookieConsents/${consentId}`), consentData);
      if (currentUser) {
        localStorage.setItem(`cookieConsentSynced_${currentUser.uid}`, "true");
      } else {
        localStorage.setItem("guestCookieConsentId", consentId);
      }
    } catch (error) {
      console.error("Error saving cookie consent to DB:", error);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-banner-wrapper">
      <div className="cookie-banner">
        <div className="cookie-banner-content">
          <div className="cookie-banner-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 11.2065 21.9082 10.4344 21.7346 9.69375C21.4395 9.89115 21.0831 10 20.7 10C19.7611 10 19 9.23888 19 8.3C19 8.16334 19.0162 8.03049 19.0469 7.90382C18.4239 7.35167 18 6.57212 18 5.7C18 4.20883 19.2088 3 20.7 3C20.803 3 20.9048 3.00574 21.0051 3.01683C18.6657 2.37877 15.4208 2 12 2Z" fill="#c1f11d" />
              <circle cx="7.5" cy="9.5" r="1.5" fill="#111" />
              <circle cx="12" cy="14" r="1" fill="#111" />
              <circle cx="8" cy="15.5" r="1" fill="#111" />
              <circle cx="15.5" cy="15.5" r="1.5" fill="#111" />
              <circle cx="15.5" cy="9.5" r="1" fill="#111" />
            </svg>
          </div>
          <div className="cookie-banner-text">
            <h4>Privacy & Cookie Consent</h4>
            <p>
              We use cookies and process personal data to enhance your experience, analyze site traffic, and support secure checkout.
              This consent is valid for 1 year (365 days) from acceptance. By clicking "Agree & Accept", you agree to our policies.
            </p>
          </div>
          <div className="cookie-banner-actions">
            <button className="cookie-btn cookie-btn-accept" onClick={handleAccept}>
              Agree & Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
